/**
 * The invoice-world switch (Rechnungen 2.0 — R2).
 *
 * THREE SHAPES, NOT ONE WITH THREE STATES. What this renders depends on how many
 * worlds the organization actually has:
 *
 *   provider "bexio"   two tabs — Bexio aktuell | Vantio Historie
 *   provider "native"  one static label — Vantio aktuell
 *   not yet known      a neutral label, or a placeholder of the same height
 *
 * WHY `native` GETS NO TABLIST. A tab list with a single tab announces a choice
 * to a screen reader and offers arrow-key navigation that goes nowhere. Worse,
 * it implies a second world exists and is merely unselected. An organization
 * that bills in Vantio has one invoice world; the honest control for one option
 * is a label, not a switch.
 *
 * NO `TabsContent`, ON PURPOSE. Radix would mount every panel it is given, and
 * mounting both panels is how a page ends up holding two invoice worlds at once.
 * This component renders the triggers; the page renders exactly one world beside
 * them — the same division `CalendarModeTabs` uses.
 *
 * The placeholder is the same height as the switch so the page does not jump
 * when the provider arrives, and it carries no word that ranks the worlds: while
 * the answer is unknown, "aktuell" and "Historie" are both unearned.
 */
import React from "react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

/** The shell the switch and its stand-ins share, so all three line up. */
const SHELL =
  "inline-flex h-9 items-center rounded-lg bg-muted p-1 text-muted-foreground";

/** A single, non-interactive world name, styled like the active tab. */
function StaticWorld({ label }) {
  return (
    <div className={SHELL}>
      <span className="rounded-md bg-background px-3 py-1 text-sm font-medium text-foreground shadow">
        {label}
      </span>
    </div>
  );
}

export default function InvoiceSourceSwitcher({
  provider,
  source,
  labels,
  onSelect,
}) {
  if (provider === "bexio") {
    return (
      <Tabs value={source} onValueChange={onSelect}>
        <TabsList aria-label="Rechnungsquelle">
          <TabsTrigger value="bexio" className="min-h-[32px] px-4">
            {labels.bexio}
          </TabsTrigger>
          <TabsTrigger value="native" className="min-h-[32px] px-4">
            {labels.native}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    );
  }

  if (provider === "native") {
    return <StaticWorld label={labels.native} />;
  }

  // Provider unknown. If an explicit `?source=native` already settled the
  // CONTENT, name it neutrally rather than leaving the reader without a heading;
  // `aria-live` so the sharper name is announced once the provider lands.
  return (
    <div aria-live="polite">
      {labels.active ? (
        <StaticWorld label={labels.active} />
      ) : (
        <div className={SHELL} aria-hidden="true">
          <span className="h-[26px] w-[180px] animate-pulse rounded-md bg-background/60" />
        </div>
      )}
    </div>
  );
}
